import { computed, effect, Injectable, signal, WritableSignal } from '@angular/core';
import { delay, Observable, of, tap } from 'rxjs';
import { ActivatePayload, AuthService, RegisterPayload } from './auth.service';
import { UserModel } from '@models/resources/user.model';
import { Role } from '@models/enums/role';

const STORAGE_KEY = 'gold_user';
const DUMMY_LATENCY_MS = 250;

/**
 * Liest den persistierten User aus `sessionStorage`. Bei korrumpiertem JSON
 * (anderer Tab, Browser-Extension) wird der Eintrag still gelöscht und `null`
 * zurückgegeben — schützt die App-Initialisierung vor `JSON.parse`-Crashes
 * (S4-1 Finding B-05).
 */
function readUserFromStorage(): UserModel | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserModel;
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

/**
 * Dummy-Auth-Service für den Demo-Mode (Sprint 1–4 FE-First-Stack).
 *
 * V4-Verhalten (Sprint 5c S5c-4):
 * - `login()` akzeptiert jede E-Mail + jedes Passwort und gibt **direkt**
 *   einen UserModel mit Pseudo-Sanctum-Token zurück. Kein 2FA-Zwischenschritt.
 * - Rolle wird heuristisch abgeleitet: `?role=staff`/`?role=customer` in der
 *   Login-URL gewinnt; fallback ist e-mail-prefix `staff*` → Staff.
 * - `register()` / `activate()` / `resendActivation()` sind No-Ops mit
 *   Latenz — reichen für die Demo-Story (FE-Klick-Throughput).
 * - `activate()` macht die Tokens nicht echt validierbar, sondern loggt den
 *   Customer direkt ein (Demo-Pfad).
 * - Tab-Close = Logout bleibt intakt (sessionStorage).
 */
@Injectable()
export class DummyAuthService extends AuthService {
  readonly user: WritableSignal<UserModel | null>;
  readonly isAuthenticated = computed(() => this.user()?.token != null);

  constructor() {
    super();
    this.user = signal<UserModel | null>(readUserFromStorage());

    effect(() => {
      const current = this.user();
      if (current) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    });
  }

  login(email: string, _password: string): Observable<UserModel> {
    const role: Role =
      readRoleOverrideFromUrl() ??
      (email.toLowerCase().startsWith('staff') ? Role.STAFF : Role.CUSTOMER);

    const user: UserModel = {
      id: 1,
      email,
      role,
      is_active: true,
      token: `dummy-${Date.now()}`,
    };

    return of(user).pipe(
      delay(DUMMY_LATENCY_MS),
      tap((u) => this.user.set(u)),
    );
  }

  register(_payload: RegisterPayload): Observable<void> {
    // No-Op + Latenz — die Bestätigungs-View wird vom Component gerendert.
    return of(void 0).pipe(delay(DUMMY_LATENCY_MS));
  }

  activate(_payload: ActivatePayload): Observable<void> {
    // Defense-in-depth-Pivot 2026-05-13: Activation gibt keinen Token
    // mehr zurück; Customer muss sich nach Aktivierung explizit auf
    // /auth/login einloggen. Dummy-Pfad spiegelt das.
    return of(void 0).pipe(delay(DUMMY_LATENCY_MS));
  }

  resendActivation(_email: string): Observable<void> {
    return of(void 0).pipe(delay(DUMMY_LATENCY_MS));
  }

  requestPasswordReset(_email: string): Observable<void> {
    return of(void 0).pipe(delay(DUMMY_LATENCY_MS));
  }

  resetPassword(_token: string, _password: string, _email: string): Observable<void> {
    return of(void 0).pipe(delay(DUMMY_LATENCY_MS));
  }

  logout(): Observable<void> {
    return of(void 0).pipe(
      delay(DUMMY_LATENCY_MS),
      tap(() => this.clear()),
    );
  }

  hydrate(): Observable<UserModel | null> {
    return of(this.user());
  }

  clear(): void {
    this.user.set(null);
  }
}

/**
 * Liest `?role=staff` / `?role=customer` aus der aktuellen URL und liefert
 * die entsprechende {@link Role}. Unbekannte Werte und SSR-Kontexte liefern
 * `undefined`, sodass die e-mail-basierte Heuristik im Login greift. Nur
 * Dummy-Modus — der `HttpAuthService` bezieht die Rolle vom Backend.
 */
function readRoleOverrideFromUrl(): Role | undefined {
  if (typeof window === 'undefined') return undefined;
  const raw = new URL(window.location.href).searchParams.get('role');
  if (raw === 'staff') return Role.STAFF;
  if (raw === 'customer') return Role.CUSTOMER;
  return undefined;
}
